// src/components/ArticlesPage.jsx

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "./SEO";

const articles = [
  {
    id: "valentines-day-card-messages",
    title: "75+ Valentine's Day Card Messages That Actually Sound Like You Wrote Them",
    author: "Emma Mitchell",
    date: "January 2026",
    excerpt: "Finding the right words for your Valentine's card? Whether you're deeply romantic or playfully sarcastic, we've got messages for every relationship style.",
    image: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=600&q=80",
    category: "Valentine's Day",
    readTime: "10 min read",
    featured: true
  },
  {
    id: "creative-valentines-cards-ideas",
    title: "15 Creative Valentine's Day Card Ideas That Go Beyond Red Hearts",
    author: "Emma Mitchell",
    date: "January 2026",
    excerpt: "Red hearts are classic, but your relationship isn't 'traditional romantic.' Discover creative card ideas for humor lovers, friends, long-distance couples, and more.",
    image: "https://images.unsplash.com/photo-1581404917879-53e19259fdda?w=600&q=80",
    category: "Valentine's Day",
    readTime: "8 min read"
  },
  {
    id: "valentines-day-photo-cards",
    title: "Valentine's Day Photo Card Tips: From Snapshot to Stunning",
    author: "Emma Mitchell",
    date: "January 2026",
    excerpt: "Turn your everyday photos into Valentine's Day masterpieces. Learn how to choose, edit, and transform photos into cards your recipient will actually want to display.",
    image: "https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=600&q=80",
    category: "Valentine's Day",
    readTime: "12 min read"
  },
  {
    id: "holiday-cards-complete-guide",
    title: "Why I Still Send Holiday Cards (And Why You Should Too)",
    author: "Emma Mitchell",
    date: "December 2024",
    excerpt: "In an era of instant messages and social media posts, holiday cards remain one of the most meaningful ways to connect. Here's my personal take on why this tradition matters.",
    image: "https://images.unsplash.com/photo-1482517967863-00e15c9b44be?w=600&q=80",
    category: "Holiday Cards",
    readTime: "8 min read"
  },
  {
    id: "ai-greeting-cards-revolution",
    title: "I Let AI Design My Holiday Cards and My Mom Thought I Hired an Artist",
    author: "Emma Mitchell",
    date: "December 2024",
    excerpt: "It started with a terrible photo. My toddler refused to wear anything except her Frozen nightgown. My husband was doing that weird smile thing. Then I discovered AI card makers...",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    category: "AI & Technology",
    readTime: "10 min read"
  },
  {
    id: "christmas-card-photo-tips",
    title: "How to Take Christmas Card Photos That Don't Make Your Family Look Unhinged",
    author: "Emma Mitchell",
    date: "December 2024",
    excerpt: "A confession: In 2022, our family Christmas card featured a photo where my son's face was 40% obscured by his own hand. Here's everything I've learned since then.",
    image: "https://images.unsplash.com/photo-1511895426328-dc8714191300?w=600&q=80",
    category: "Photography Tips",
    readTime: "12 min read"
  },
  {
    id: "holiday-card-message-ideas",
    title: "100+ Holiday Card Messages for Every Situation",
    author: "Editorial Team",
    date: "December 2024",
    excerpt: "Staring at a blank card with a pen in hand, mind completely empty? Copy-paste ready messages for family, friends, coworkers, and every relationship in between.",
    image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&q=80",
    category: "Writing Tips",
    readTime: "15 min read"
  },
  {
    id: "eco-friendly-holiday-cards",
    title: "Sustainable Holiday Cards: How to Send Greetings Without the Guilt",
    author: "Editorial Team",
    date: "December 2024",
    excerpt: "Every December, 2 billion cards are sent in the US alone. Here's how to maintain this tradition with a smaller environmental footprint.",
    image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=600&q=80",
    category: "Sustainability",
    readTime: "9 min read"
  },
  {
    id: "diy-card-ideas",
    title: "10 DIY Card Ideas That Look Store-Bought (But Aren't)",
    author: "Emma Mitchell",
    date: "December 2024",
    excerpt: "You don't need to be crafty to make cards that impress. These simple DIY techniques will have everyone asking where you bought them.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    category: "DIY Ideas",
    readTime: "7 min read"
  },
  {
    id: "last-minute-card-guide",
    title: "The Procrastinator's Guide to Last-Minute Holiday Cards",
    author: "Emma Mitchell", 
    date: "December 2024",
    excerpt: "It's December 20th and you haven't sent a single card. Don't panic. Here's how to pull off a holiday card miracle in 48 hours or less.",
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=600&q=80",
    category: "Holiday Cards",
    readTime: "5 min read"
  }
];

export default function ArticlesPage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="The Card Corner - Greeting Card Tips & Inspiration"
        description="Discover tips, inspiration, and advice for creating meaningful greeting cards. Holiday card guides, message ideas, photography tips, and DIY inspiration from DreamableAI."
        path="/articles"
      />
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              The Card Corner
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Tips, inspiration, and honest advice for creating meaningful greeting cards
            </p>
          </div>
        </section>

      {/* Featured Article - Valentine's Day Theme */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-12">
        <Link 
          to={`/articles/${articles[0].id}`}
          className="block bg-gradient-to-br from-white to-pink-50 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 border border-pink-100"
        >
          <div className="md:flex">
            <div className="md:w-1/2 relative">
              <img 
                src={articles[0].image} 
                alt={articles[0].title}
                className="w-full h-64 md:h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-bold rounded-full shadow-lg">
                  <span>💕</span>
                  <span>Valentine's Special</span>
                </span>
              </div>
            </div>
            <div className="md:w-1/2 p-6 md:p-10 flex flex-col justify-center">
              <span className="inline-block px-3 py-1 bg-rose-100 text-rose-700 text-sm font-medium rounded-full mb-4 w-fit">
                {articles[0].category}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 hover:text-rose-600 transition-colors">
                {articles[0].title}
              </h2>
              <p className="text-gray-600 mb-4 text-lg">
                {articles[0].excerpt}
              </p>
              <div className="flex items-center text-sm text-gray-500">
                <span className="font-medium text-gray-700">{articles[0].author}</span>
                <span className="mx-2">•</span>
                <span>{articles[0].date}</span>
                <span className="mx-2">•</span>
                <span>{articles[0].readTime}</span>
              </div>
            </div>
          </div>
        </Link>
      </section>

      {/* Valentine's Day Quick Links */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 rounded-2xl p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                💝 Ready to Create Your Valentine's Card?
              </h3>
              <p className="text-pink-100">
                Browse beautiful templates or let AI design something unique
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/occasions/valentines-day"
                className="px-6 py-3 bg-white text-rose-600 font-bold rounded-full hover:bg-pink-50 transition-colors shadow-lg"
              >
                Browse Templates
              </Link>
              <Link
                to="/"
                className="px-6 py-3 bg-white/20 text-white font-bold rounded-full hover:bg-white/30 transition-colors border border-white/30"
              >
                Create with AI
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Article Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
          Latest Articles
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.slice(1).map((article) => (
            <Link
              key={article.id}
              to={`/articles/${article.id}`}
              className="group bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm text-purple-700 text-xs font-medium rounded-full">
                  {article.category}
                </span>
              </div>
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="font-medium">{article.author}</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-600 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Create Your Own Cards?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Transform your photos into stunning greeting cards with our AI-powered tools
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-8 py-4 bg-white text-purple-600 font-bold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
          >
            Start Creating
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Newsletter Section - Hidden for now
        <section className="bg-gray-50 py-16">
          ...
        </section>
        */}
      </div>
    </>
  );
}
